import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, FileQuestion, GitBranch, PenTool } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useBranding } from '../../contexts/BrandingContext';
import Routes from '../../routes/Routes';
import { useAppSelector } from '../../stores';
import {
    ADMIN_ONBOARDING_PENDING_KEY,
    ADMIN_ONBOARDING_TARGET_KEY,
} from '../../utils/adminOnboardingStorage';
import './admin-onboarding.css';

type Goal = 'draw' | 'map' | 'survey';
type Step = 0 | 1 | 2;

const goalTargets: Record<Goal, string> = {
    draw: `${Routes.HOMEVIEW}?createDiagram=draw`,
    map: `${Routes.HOMEVIEW}?createDiagram=interview`,
    survey: Routes.DATA_COLLECT,
};

const AdminOnboardingView = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const users = useAppSelector((state) => state.auth);
    const { branding } = useBranding();
    const [step, setStep] = useState<Step>(0);
    const [goal, setGoal] = useState<Goal | null>(null);

    useEffect(() => {
        if (!users.session.company_id) {
            navigate(Routes.CREATE_MY_COMPANY, { replace: true });
            return;
        }
        if (sessionStorage.getItem(ADMIN_ONBOARDING_PENDING_KEY) !== 'true') {
            navigate(Routes.HOMEVIEW, { replace: true });
        }
    }, [navigate, users.session.company_id]);

    const firstName =
        (users.user.fullname || '').trim().split(/\s+/)[0] ||
        t('views.adminOnboarding.userFallback');

    const selectedGoal = useMemo(() => {
        if (!goal) return null;
        return {
            label: t(`views.adminOnboarding.goals.${goal}.title`),
            title: t(`views.adminOnboarding.goals.${goal}.readyTitle`),
            head: t(`views.adminOnboarding.goals.${goal}.readyHead`),
            description: t(`views.adminOnboarding.goals.${goal}.readyDescription`),
            cta: t(`views.adminOnboarding.goals.${goal}.cta`),
        };
    }, [goal, t]);

    const continueFunnel = (target?: string) => {
        sessionStorage.removeItem(ADMIN_ONBOARDING_PENDING_KEY);
        if (target) sessionStorage.setItem(ADMIN_ONBOARDING_TARGET_KEY, target);
        else sessionStorage.removeItem(ADMIN_ONBOARDING_TARGET_KEY);
        navigate(`${Routes.COMAPNYINVITED}/auth`, { replace: true });
    };

    const skip = () => continueFunnel();

    const finish = () => {
        if (!goal) return;
        continueFunnel(goalTargets[goal]);
    };

    const cards: Array<{ id: Goal; icon: React.ReactNode }> = [
        { id: 'draw', icon: <PenTool size={22} /> },
        { id: 'map', icon: <GitBranch size={22} /> },
        { id: 'survey', icon: <FileQuestion size={22} /> },
    ];

    return (
        <main className="admin-onboarding">
            <header className="admin-onboarding__header">
                <span className="admin-onboarding__brand">{branding.appName || 'Famla'}</span>
                {step > 0 && (
                    <div className="admin-onboarding__progress" aria-label={t('views.adminOnboarding.progress', { step })}>
                        <span className={step >= 1 ? 'active' : ''} />
                        <span className={step >= 2 ? 'active' : ''} />
                        <b>{t('views.adminOnboarding.progress', { step })}</b>
                    </div>
                )}
                <button type="button" className="admin-onboarding__skip" onClick={skip}>
                    {t('views.adminOnboarding.skip')}
                </button>
            </header>

            <section className="admin-onboarding__stage">
                <div className="admin-onboarding__panel" key={step}>
                    {step === 0 && (
                        <div className="admin-onboarding__welcome">
                            <span className="admin-onboarding__mark" aria-hidden="true">◆</span>
                            <h1>{t('views.adminOnboarding.welcomeTitle', { name: firstName })}</h1>
                            <p>{t('views.adminOnboarding.welcomeDescription')}</p>
                            <button type="button" className="admin-onboarding__primary" onClick={() => setStep(1)}>
                                {t('views.adminOnboarding.setup')} <ArrowRight size={18} />
                            </button>
                            <small>{t('views.adminOnboarding.duration')}</small>
                        </div>
                    )}

                    {step === 1 && (
                        <div className="admin-onboarding__goal-step">
                            <div className="admin-onboarding__title">
                                <h1>{t('views.adminOnboarding.goalTitle')}</h1>
                                <p>{t('views.adminOnboarding.goalSubtitle')}</p>
                            </div>
                            <div className="admin-onboarding__goals">
                                {cards.map(({ id, icon }) => (
                                    <button
                                        key={id}
                                        type="button"
                                        className={`admin-onboarding__goal admin-onboarding__goal--${id}${goal === id ? ' selected' : ''}`}
                                        onClick={() => setGoal(id)}
                                        aria-pressed={goal === id}
                                    >
                                        <span className="admin-onboarding__goal-top">
                                            <span className="admin-onboarding__goal-icon">{icon}</span>
                                            {id === 'draw' && <small>{t('views.adminOnboarding.new')}</small>}
                                            {id === 'map' && <small>{t('views.adminOnboarding.recommended')}</small>}
                                        </span>
                                        <strong>{t(`views.adminOnboarding.goals.${id}.title`)}</strong>
                                        <span>{t(`views.adminOnboarding.goals.${id}.description`)}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {step === 2 && goal && selectedGoal && (
                        <div className="admin-onboarding__ready">
                            <span className="admin-onboarding__check" aria-hidden="true"><Check size={30} /></span>
                            <h1>{selectedGoal.title}</h1>
                            <p>{t('views.adminOnboarding.readyDescription')}</p>
                            <div className="admin-onboarding__summary">
                                <div>
                                    <span>{t('views.adminOnboarding.yourGoal')}</span>
                                    <strong>{selectedGoal.label}</strong>
                                </div>
                                <div className="admin-onboarding__next">
                                    <span className={`admin-onboarding__goal-icon admin-onboarding__goal-icon--${goal}`}>
                                        {cards.find((card) => card.id === goal)?.icon}
                                    </span>
                                    <span>
                                        <strong>{selectedGoal.head}</strong>
                                        <small>{selectedGoal.description}</small>
                                    </span>
                                </div>
                            </div>
                            <button type="button" className="admin-onboarding__primary" onClick={finish}>
                                {selectedGoal.cta} <ArrowRight size={18} />
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {step === 1 && (
                <footer className="admin-onboarding__footer">
                    <div>
                        <button type="button" className="admin-onboarding__back" onClick={() => setStep(0)}>
                            <ArrowLeft size={17} /> {t('words.back')}
                        </button>
                        <button
                            type="button"
                            className="admin-onboarding__primary"
                            onClick={() => setStep(2)}
                            disabled={!goal}
                        >
                            {t('views.adminOnboarding.continue')} <ArrowRight size={18} />
                        </button>
                    </div>
                </footer>
            )}
        </main>
    );
};

export default AdminOnboardingView;
